import { Browser } from '@capacitor/browser';

// Whether the app is running inside the native shell (Capacitor) or a browser.
//
// Read from the global the native runtime injects, not from an import of
// @capacitor/core, so it answers correctly in every build: the web build never
// has it, and a native build always does.
//
// isNativePlatform(), not the mere presence of window.Capacitor: the web
// fallback of @capacitor/core also defines window.Capacitor and reports
// "web", and treating that as native would switch off the web app's service
// worker for every browser user.
export function isNativeApp(win = typeof window !== 'undefined' ? window : undefined) {
  try {
    return Boolean(win?.Capacitor?.isNativePlatform?.());
  } catch {
    return false;
  }
}

/**
 * Stop iOS zooming the page when a text field is focused, in the native app
 * only.
 *
 * iOS zooms in on any focused input whose font is under 16px, and the design
 * package's .field is 0.95rem. In the native app the zoom outlived the sign-in
 * form: every screen after it was drawn larger than the phone, with the menu
 * button cut off at the right edge. maximum-scale=1 stops it. It is applied
 * only inside the app, which does not pinch-zoom anyway. In a browser it would
 * also take away pinch-zoom on Android, which people rely on to read.
 *
 * The proper fix is 16px fields in @loenhart/design, which also stops the same
 * zoom for iPhone users of the website.
 */
export function lockNativeZoom(doc = typeof document !== 'undefined' ? document : undefined, native = isNativeApp()) {
  if (!native || !doc) return false;
  const meta = doc.querySelector('meta[name="viewport"]');
  if (!meta) return false;
  const content = meta.getAttribute('content') || '';
  if (!/maximum-scale/.test(content)) {
    meta.setAttribute('content', `${content}, maximum-scale=1`);
  }
  return true;
}

/**
 * Open an external link without leaving the app, in the native app only.
 *
 * Capacitor's own WKWebView delegate (`WebViewDelegationHandler.swift`) answers
 * every `target="_blank"` anchor and `window.open()` call with
 * `UIApplication.shared.open(url)` — that backgrounds this app entirely and
 * switches to standalone Safari. For a demo video's "Watch on YouTube"
 * fallback, that means losing the workout to watch a video and having to
 * switch back by hand.
 *
 * `@capacitor/browser`'s `Browser.open` presents the same URL in an
 * `SFSafariViewController` sheet instead: it sits over the app and the person
 * dismisses it back into exactly where they were. `fullscreen`/the navy
 * toolbar color just keep that sheet from looking like a different app.
 *
 * Returns whether it handled the open, so a caller knows whether to also
 * `preventDefault()` the anchor's own `target="_blank"` handling — which is
 * the backgrounding path above, and must not also fire.
 */
export function openExternal(url, native = isNativeApp(), open = (options) => Browser.open(options)) {
  if (!native) return false;
  open({ url, presentationStyle: 'fullscreen', toolbarColor: '#030914' });
  return true;
}
